import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import * as yup from "yup";

import { Grid } from "@mui/material";
import { useDespatchActions } from "../../views/DiaryResourcesView/hooks/useDespatchActions";
import Button, { Color } from "Components/Form/Button";
import RadioButton from "Components/Form/RadioGroup";
import { isBefore, add, isSameDay } from "date-fns";
import { DespatchTask } from "Models/despatch.task";
import { Task, TaskStartDatePeriod } from "Models/task";
import { useTranslation } from "react-i18next";
import { getMuiPopupContainer } from "Themes/dispatchTheme";
import { TaskStatus, TimeSlotType } from "Util/enums";
import {
  AFTERNOON_END,
  AFTERNOON_START,
  EVENING_END,
  EVENING_START,
  MORNING_END,
  MORNING_START,
  TASK_DATE_MINUTES,
} from "Util/startDate";

import { ChangeTaskTimeDefaultPeriodForm } from "./ChangeTaskTimeDefaultPeriodForm";
import { ChangeTaskTimeMultiDayForm } from "./ChangeTaskTimeMultiDayForm";
import { MultiDayForm, DrawerFooter } from "./styles";
import { AppDrawer, AppDrawerProps } from "Components/Generic/AppDrawer";
import { VariableSlotTime } from "../../../../../../pages/private/Moving/Operations/Maintenance/Tasks/VariableSlotTime";
import { zonedTimeToUtc } from "date-fns-tz";
import { TimeSlotTask } from "Models/time.slot.task";
import { PERIOD_BUFFER_TIME } from "Constants";
import { formatSimpleDate, formatSimpleTime } from "Util/formatDateAndTime";
import { changeTaskTimeSchema } from "./schemaValidation";
import { useOperationDespatchContext } from "../../../../contexts/DespatchContext";
import TaskDateUtils from "Util/dateUtils";
import { useAppState } from "Context/StateContext";

export interface DespatchChangeTimeModalHandles {
  open: (task: DespatchTask | Task) => void;
  close: () => void;
}

interface Props {
  onSuccessTaskUpdated?: (variables: Task) => void;
}

const ChangeTimeModalComponent: ForwardRefRenderFunction<DespatchChangeTimeModalHandles, Props> = (
  { onSuccessTaskUpdated },
  ref
) => {
  const { t: translate } = useTranslation();
  const { formatWithUserTimezone, userTimezone } = useAppState();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [task, setTask] = useState<DespatchTask | Task>();

  const [whatHasChanged, setWhatHasChanged] = useState<string[]>([]);
  const [slotTime, setSlotTime] = useState<TimeSlotType>(TimeSlotType.NONE);
  const [errors, setErrors] = useState<yup.ValidationError[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const { useUpdateTask } = useDespatchActions();
  const { currentDate } = useOperationDespatchContext();

  const { mutate: updateTask, isLoading: isUpdatingTask } = useUpdateTask((variables) =>
    onSuccessTaskUpdated?.(variables)
  );

  const startTime = useMemo(
    () => (task?.startDate ? formatSimpleTime(task?.startDate, { isUTC: true }) : null),
    [task?.startDate]
  );

  const endTime = useMemo(
    () => (task?.endDate ? formatSimpleTime(task?.endDate, { isUTC: true }) : null),
    [task?.endDate]
  );

  useEffect(() => {
    if (task?.period) {
      if (task.period === TaskStartDatePeriod.FIXED_TIME) setSlotTime(TimeSlotType.FIXED);
      else if (task.period === TaskStartDatePeriod.VARIABLE_TIME)
        setSlotTime(TimeSlotType.VARIABLE);
    }
  }, [openModal, task]);

  useEffect(() => {
    if (!task || !task.endDate || !task.startDate) return;

    let taskEndDate = new Date(task.endDate);
    const taskStartDate = new Date(task.startDate);

    if (
      isBefore(taskEndDate, taskStartDate) ||
      (task.period &&
        [
          TaskStartDatePeriod.MULTI_DAY,
          TaskStartDatePeriod.FIXED_TIME,
          TaskStartDatePeriod.VARIABLE_TIME,
        ].includes(task.period) &&
        isSameDay(taskEndDate, taskStartDate))
    ) {
      taskEndDate = new Date(taskStartDate);
      taskEndDate = add(taskEndDate, PERIOD_BUFFER_TIME[task?.period as TaskStartDatePeriod]);

      setTask(
        (oldTask) =>
          ({
            ...oldTask,
            endDate: taskEndDate.toISOString(),
          } as DespatchTask)
      );
    }
  }, [task?.period, task?.startDate, task?.endDate]);

  const isTaskPublished = useMemo(() => {
    return task?.status?.toLowerCase() !== TaskStatus.REGISTERED?.toLowerCase();
  }, [task]);

  const open = useCallback((task: DespatchTask | Task) => {
    setTask(task);
    setOpenModal(true);
  }, []);

  const close = useCallback(() => {
    setIsSubmitted(false);
    setSlotTime(TimeSlotType.NONE);
    setTask(undefined);
    setOpenModal(false);
  }, []);

  const isMultiDayTask = useMemo(() => {
    if (!task) return false;

    return [
      TaskStartDatePeriod.MULTI_DAY,
      TaskStartDatePeriod.FIXED_TIME,
      TaskStartDatePeriod.VARIABLE_TIME,
    ].includes(task?.period!);
  }, [task?.period]);

  const handleValidateTask = () => {
    try {
      changeTaskTimeSchema({ translate, formatFunction: formatWithUserTimezone }).validateSync(
        {
          startDate: task?.startDate,
          endDate: task?.endDate,
          period: task?.period,
          timeSlots: task?.timeSlots,
          startTime,
          endTime,
          isMultiDayTask,
        },
        { abortEarly: false }
      );
      setErrors([]);
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        setErrors(error.inner);
        return false;
      }
    }
  };

  const getStartAndEndDate = (task: Task | DespatchTask) => {
    if (!task.timeSlots?.length) {
      return { startDate: task.startDate, endDate: task.endDate };
    }

    const currentTimeSlots = task.timeSlots.sort(
      (a, b) =>
        zonedTimeToUtc(a.date!, userTimezone.name).getTime() -
        zonedTimeToUtc(b.date!, userTimezone.name).getTime()
    );

    const startCurrentDate = formatSimpleDate(new Date(currentTimeSlots?.[0]?.date as string), {
      formatString: "yyyy-MM-dd",
      isUTC: true,
    });

    const indexOfLastDate = currentTimeSlots?.length - 1;
    const endCurrentDate = formatSimpleDate(
      new Date(currentTimeSlots?.[indexOfLastDate]?.date as string),
      { formatString: "yyyy-MM-dd", isUTC: true }
    );

    const startCurrentTime = currentTimeSlots?.[0]?.from;
    const currentStartDate = new Date(`${startCurrentDate}T${startCurrentTime}`);

    const startDate = zonedTimeToUtc(currentStartDate, userTimezone.name).toISOString();

    const endCurrentTime = currentTimeSlots?.[indexOfLastDate]?.from;
    const currentEndDate = new Date(`${endCurrentDate}T${endCurrentTime}`);

    const endDate = zonedTimeToUtc(currentEndDate, userTimezone.name).toISOString();

    return {
      startDate,
      endDate,
    };
  };

  const onSubmit = () => {
    setIsSubmitted(true);
    const isValid = handleValidateTask();
    if (!isValid) return;

    if (task && task.id) {
      let startDate = task.startDate;
      let endDate = task.endDate;

      if (slotTime === TimeSlotType.VARIABLE && task?.timeSlots?.length) {
        const { startDate: currentStartDate, endDate: currentEndDate } = getStartAndEndDate(task);
        startDate = currentStartDate;
        endDate = currentEndDate;
      }

      updateTask(
        {
          taskId: task?.id,
          task: { ...task, startDate, endDate, whatHasChanged } as Task & {
            whatHasChanged?: string[];
          },
        },
        {
          onSuccess: () => {
            close();
          },
        }
      );
    }
  };

  useEffect(() => {
    if (isSubmitted) handleValidateTask();
  }, [task, isSubmitted]);

  useImperativeHandle(ref, () => ({
    open,
    close,
  }));

  const modalTitle = useMemo(() => {
    if (task?.isPlaceholder) {
      return `${translate("timeFor")} ${task?.name}`;
    }

    if (!task?.period) return `${translate("timeFor")} #${task?.job.reference} - ${task?.type}`;

    if (
      [
        TaskStartDatePeriod.MULTI_DAY,
        TaskStartDatePeriod.VARIABLE_TIME,
        TaskStartDatePeriod.FIXED_TIME,
      ].includes(task?.period)
    ) {
      return `${translate("dateFor")} #${task?.job.reference} - ${task?.type}`;
    }

    return `${translate("timeFor")} #${task?.job.reference} - ${task?.type}`;
  }, [task]);

  const renderFormBody = () => {
    if (!task) return;
    switch (task.period) {
      case TaskStartDatePeriod.MULTI_DAY:
        return (
          <ChangeTaskTimeMultiDayForm
            setTask={setTask}
            task={task}
            isLoading={isUpdatingTask}
            setWhatHasChanged={(field) => setWhatHasChanged((prev) => [...(prev || []), field])}
            errors={errors}
          />
        );
      case TaskStartDatePeriod.FIXED_TIME:
        return (
          <ChangeTaskTimeMultiDayForm
            setTask={setTask}
            task={task}
            isLoading={isUpdatingTask}
            showTimeFields={true}
            setWhatHasChanged={(field) => setWhatHasChanged((prev) => [...(prev || []), field])}
            errors={errors}
          />
        );
      case TaskStartDatePeriod.VARIABLE_TIME:
        return (
          <VariableSlotTime
            task={task as Task}
            setTask={(item) => setTask((prev) => ({ ...(prev || {}), ...(item || {}) } as Task))}
            errors={errors}
            currentDate={currentDate}
          />
        );

      default:
        return (
          <ChangeTaskTimeDefaultPeriodForm
            setTask={setTask}
            task={task}
            isLoading={isUpdatingTask}
            setWhatHasChanged={(field) => setWhatHasChanged((prev) => [...(prev || []), field])}
            errors={errors}
          />
        );
    }
  };

  const handleChangePeriod = (periodValue: TaskStartDatePeriod) => {
    const startDate = task?.startDate ? new Date(task?.startDate) : new Date();
    let endDate;

    const aux = new Date(startDate);

    if (periodValue === TaskStartDatePeriod.MULTI_DAY) {
      endDate = add(aux, { days: 1 });
    } else {
      endDate = add(aux, { hours: 1 });
    }

    switch (periodValue) {
      case TaskStartDatePeriod.MORNING:
        startDate.setHours(MORNING_START);
        startDate.setMinutes(TASK_DATE_MINUTES);

        endDate.setHours(MORNING_END);
        endDate.setMinutes(TASK_DATE_MINUTES);
        break;
      case TaskStartDatePeriod.AFTERNOON:
        startDate.setHours(AFTERNOON_START);
        startDate.setMinutes(TASK_DATE_MINUTES);

        endDate.setHours(AFTERNOON_END);
        endDate.setMinutes(TASK_DATE_MINUTES);
        break;
      case TaskStartDatePeriod.EVENING:
        startDate.setHours(EVENING_START);
        startDate.setMinutes(TASK_DATE_MINUTES);

        endDate.setHours(EVENING_END);
        endDate.setMinutes(TASK_DATE_MINUTES);
        break;
      case TaskStartDatePeriod.FULL_DAY:
        startDate.setHours(MORNING_START);
        startDate.setMinutes(TASK_DATE_MINUTES);

        endDate.setHours(AFTERNOON_END);
        endDate.setMinutes(TASK_DATE_MINUTES);
        break;
      default:
    }

    const newStartDateIso = startDate.toISOString();
    const newEndDateIso = endDate.toISOString();

    setTask(
      (prev) =>
        ({
          ...prev,
          period: periodValue,
          startDate: newStartDateIso,
          endDate: newEndDateIso,
        } as Task)
    );
  };

  const renderMultiDayRadio = () => {
    return (
      <RadioButton
        label={translate("isThisAMultiDayTask")}
        onChange={(event) => {
          setSlotTime(TimeSlotType.NONE);
          setTask((prev) => ({ ...(prev || {}), timeSlots: [] } as Task));

          const rawValue = event.target.value;

          const parsedValue = rawValue.includes(",")
            ? rawValue.split(",").map((value) => Number(value.trim()))
            : Number(rawValue);

          handleChangePeriod(
            (Array.isArray(parsedValue) ? parsedValue.at(0) : parsedValue) ??
              TaskStartDatePeriod.MORNING
          );
        }}
        options={[
          {
            label: translate("yes"),
            value: [
              TaskStartDatePeriod.MULTI_DAY,
              TaskStartDatePeriod.FIXED_TIME,
              TaskStartDatePeriod.VARIABLE_TIME,
            ],
          },
          {
            label: translate("no"),
            value: [
              TaskStartDatePeriod.MORNING,
              TaskStartDatePeriod.AFTERNOON,
              TaskStartDatePeriod.EVENING,
              TaskStartDatePeriod.FULL_DAY,
              TaskStartDatePeriod.CUSTOM,
            ],
          },
        ]}
        value={task?.period}
        disabled={isTaskPublished}
      />
    );
  };

  const renderSlotTimeShowing = () => (
    <RadioButton
      label={translate("isTimeSlotRequiredForMultiDayTask")}
      onChange={(event) => {
        const value = event.target.value;
        if (value === TimeSlotType.VARIABLE) {
          setTask(
            (prev) =>
              ({
                ...(prev || {}),
                period: TaskStartDatePeriod.VARIABLE_TIME,
                timeSlots: [
                  ...(prev?.timeSlots || []),
                  {
                    date: TaskDateUtils.createTaskDateTime(
                      {
                        dateInput: TaskDateUtils.getTodayForInput(currentDate),
                        timeInput: "09:00",
                      },
                      userTimezone.name
                    ),
                  } as TimeSlotTask,
                ],
              } as Task)
          );
        } else {
          const fixedTimeSlotPeriod =
            value === TimeSlotType.FIXED
              ? TaskStartDatePeriod.FIXED_TIME
              : TaskStartDatePeriod.MULTI_DAY;
          setTask(
            (prev) => ({ ...(prev || {}), period: fixedTimeSlotPeriod, timeSlots: [] } as Task)
          );
        }

        setSlotTime(event.target.value as TimeSlotType);
      }}
      value={slotTime}
      options={[
        { label: translate("no"), value: TimeSlotType.NONE },
        {
          label: translate("fixedTimeSlot"),
          value: TimeSlotType.FIXED,
        },
        {
          label: translate("variableTimeSlot"),
          value: TimeSlotType.VARIABLE,
        },
      ]}
    />
  );

  const drawerSections: AppDrawerProps["sectionItems"] = [
    {
      id: "modal-time",
      content: (
        <Grid container spacing={2}>
          <MultiDayForm item xs={12}>
            {renderMultiDayRadio()}
          </MultiDayForm>
          {task?.period &&
            [
              TaskStartDatePeriod.MULTI_DAY,
              TaskStartDatePeriod.FIXED_TIME,
              TaskStartDatePeriod.VARIABLE_TIME,
            ].includes(task?.period) && (
              <Grid item xs={12}>
                {renderSlotTimeShowing()}
              </Grid>
            )}
          {renderFormBody()}
        </Grid>
      ),
    },
  ];

  return (
    <AppDrawer
      DrawerProps={{
        open: openModal,
        onClose: close,
        container: getMuiPopupContainer,
      }}
      title={modalTitle}
      hideSideMenu
      disableModalPortal={false}
      footer={
        <DrawerFooter>
          <Button customColor={Color.SEND} disabled={isUpdatingTask} onClick={() => close()}>
            {translate("close")}
          </Button>
          <Button customColor={Color.BRAND} onClick={onSubmit} loading={isUpdatingTask}>
            {translate("update")}
          </Button>
        </DrawerFooter>
      }
      sectionItems={drawerSections}
    />
  );
};

const ChangeTimeModal = forwardRef(ChangeTimeModalComponent);

export { ChangeTimeModal };
